import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowBack } from '@mui/icons-material';
import {
    Box,
    Button,
    FormHelperText,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import {
    useGetContractorPreferencesQuery,
    useUpdateContractorPreferencesMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import FormSelect from '../../shared/FormSelect';
import toast from '../../shared/Toast';

const Schema = z.object({
    requestReminderInterval: z.number().min(0).max(7),
});

const ContractorNotifications = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    type FormValues = z.infer<typeof Schema>;

    const methods = useForm<FormValues>({
        defaultValues: {
            requestReminderInterval: 1,
        },
        resolver: zodResolver(Schema),
    });

    const { data: preferences } = useGetContractorPreferencesQuery(
        loggedInUser?.company,
        { skip: !loggedInUser },
    );

    const [updatePreferences] = useUpdateContractorPreferencesMutation();

    const { control, handleSubmit, reset } = methods;

    useEffect(() => {
        if (preferences) reset(preferences);
    }, [preferences]);

    const onSubmit = async (data: FormValues) => {
        try {
            if (!loggedInUser?.company) return;

            await updatePreferences([loggedInUser?.company, data]).unwrap();

            toast.success('Notification preferences updated successfully');
        } catch (error) {
            toast.error('Error updating notification preferences');
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Typography variant='h1'>Notification Preferences</Typography>
                <Stack spacing={1} sx={{ pt: '30px' }}>
                    <Typography variant='subHeading'>
                        Job Notifications
                    </Typography>
                    <div>
                        <InputLabel>Pending Job Request Reminders</InputLabel>
                        <FormSelect
                            name='requestReminderInterval'
                            control={control}
                            options={[
                                {
                                    value: 0,
                                    label: 'Never',
                                },
                                {
                                    value: 1,
                                    label: 'Daily',
                                },
                                {
                                    value: 2,
                                    label: 'Every other day',
                                },
                                {
                                    value: 3,
                                    label: 'Every 3 days',
                                },
                                {
                                    value: 7,
                                    label: 'Weekly',
                                },
                            ]}
                        />
                        <FormHelperText>
                            You’ll only receive reminders if you have any
                            requests pending over 24 hours
                        </FormHelperText>
                    </div>
                </Stack>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: '50px',
                    }}
                >
                    <CustomButton
                        startIcon={<ArrowBack />}
                        size='small'
                        onClick={() => navigate(-1)}
                        color='primary'
                    >
                        <Typography sx={{ fontSize: '12px' }}>Back</Typography>
                    </CustomButton>
                    <Button variant='contained' type='submit'>
                        Save
                    </Button>
                </Box>
            </form>
        </FormProvider>
    );
};

export default ContractorNotifications;
